/**
 * Products > Client prices: URL state (D49).
 *
 *   view=client-prices                  the Client prices view (absent = Catalogue)
 *   tab=products|services               the sub-tab (DESIGN.md §12.1.1: `?tab=`)
 *   pfor=<client org id>                Prices for (absent = All clients)
 *   pq=<text>                           search (SKU code and title)
 *   pskus=<code,code,...>               exactly these SKUs (a bill's Set price, D25)
 *   pmore=1                             the bill had more priceless SKUs than it listed
 *   pfocus=sku:<code> | svc:<item key>  focus that row's All clients cell once
 *
 * Every value is validated and falls back: an unknown tab or client never
 * blanks the grid. The `p` prefix mirrors the usage report's `u*` params so
 * the two never collide with a catalogue param. Pure: no React.
 */
import { THREE_PL_BUILTIN_KEYS, type ThreePlBuiltinKey } from '@threadheads/shared';
import { updateSearchParams } from '@/lib/search-params';

export const PRODUCTS_VIEW_PARAM = 'view';
export const CLIENT_PRICES_VIEW = 'client-prices';

export const CLIENT_PRICES_TABS = ['products', 'services'] as const;
export type ClientPricesTab = (typeof CLIENT_PRICES_TABS)[number];

export const PRICES_PARAM = {
  tab: 'tab',
  pricesFor: 'pfor',
  search: 'pq',
  skus: 'pskus',
  /** `1`: the bill listed only the first `PSKUS_MAX` of its priceless SKUs (the server's cap). */
  more: 'pmore',
  focus: 'pfocus',
} as const;

/** A bill's Set price lists at most this many SKUs (D25). */
export const PSKUS_MAX = 50;

/**
 * The grid's item key of one of the 3PL's own services, `svc:<uuid>`. Here,
 * not in the grid's state module, because the 3PL bill (Reports, in the main
 * bundle) builds Set price links with it and must not pull that module in.
 */
export const serviceItemKey = (serviceId: string): string => `svc:${serviceId}`;

/** The grid's item key of a built-in, `builtin:printing` or `builtin:pick_pack` (D14). */
export const builtinItemKey = (key: ThreePlBuiltinKey): string => `builtin:${key}`;

/**
 * A row to focus on arrival. `itemKey` is the grid's own format for a
 * service row: `builtin:printing`, `builtin:pick_pack` or `svc:<uuid>`.
 */
export type PriceFocus = { kind: 'sku'; sku: string } | { kind: 'service'; itemKey: string };

export interface ClientPricesUrlState {
  /** A known tab, or null (the page picks the first non-empty one). */
  tab: ClientPricesTab | null;
  /** The raw client id; the page checks it against the grid's clients. */
  pricesFor: string | null;
  search: string;
  /** At most `PSKUS_MAX` codes, de-duplicated, or null when absent or empty. */
  skus: string[] | null;
  /**
   * Only the first SKUs are shown: the link listed more than `PSKUS_MAX`, or
   * it says (`pmore=1`) the bill itself held more than it listed. False
   * without `pskus`.
   */
  skusCapped: boolean;
  focus: PriceFocus | null;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const BUILTIN_ITEM_KEYS: ReadonlySet<string> = new Set(THREE_PL_BUILTIN_KEYS.map(builtinItemKey));

export function parsePriceFocus(raw: string | null): PriceFocus | null {
  const value = raw?.trim() ?? '';
  if (value.startsWith('sku:')) {
    const sku = value.slice(4).trim();
    return sku === '' ? null : { kind: 'sku', sku };
  }
  if (value.startsWith('svc:')) {
    const key = value.slice(4).trim();
    if (BUILTIN_ITEM_KEYS.has(key)) return { kind: 'service', itemKey: key };
    if (UUID_RE.test(key)) return { kind: 'service', itemKey: serviceItemKey(key.toLowerCase()) };
  }
  return null;
}

/** `sku:<code>`, `svc:builtin:printing` or, for an own service, `svc:<uuid>` (never `svc:svc:`). */
export function serializePriceFocus(focus: PriceFocus): string {
  if (focus.kind === 'sku') return `sku:${focus.sku}`;
  return focus.itemKey.startsWith('svc:') ? focus.itemKey : `svc:${focus.itemKey}`;
}

function parseSkuList(raw: string | null): { skus: string[] | null; capped: boolean } {
  if (raw === null) return { skus: null, capped: false };
  const seen = new Set<string>();
  const skus: string[] = [];
  for (const part of raw.split(',')) {
    const code = part.trim();
    const norm = code.toUpperCase();
    if (code === '' || seen.has(norm)) continue;
    seen.add(norm);
    skus.push(code);
  }
  if (skus.length === 0) return { skus: null, capped: false };
  return { skus: skus.slice(0, PSKUS_MAX), capped: skus.length > PSKUS_MAX };
}

export function readClientPricesParams(params: URLSearchParams): ClientPricesUrlState {
  const tabRaw = params.get(PRICES_PARAM.tab)?.trim().toLowerCase() ?? '';
  const tab = (CLIENT_PRICES_TABS as readonly string[]).includes(tabRaw)
    ? (tabRaw as ClientPricesTab)
    : null;
  const pricesFor = params.get(PRICES_PARAM.pricesFor)?.trim() || null;
  const { skus, capped } = parseSkuList(params.get(PRICES_PARAM.skus));
  const more = params.get(PRICES_PARAM.more)?.trim() === '1';
  return {
    tab,
    pricesFor,
    search: params.get(PRICES_PARAM.search) ?? '',
    skus,
    skusCapped: skus !== null && (capped || more),
    focus: parsePriceFocus(params.get(PRICES_PARAM.focus)),
  };
}

/** The URL's tab, else the first non-empty one (DESIGN.md §12.1.1). Services always has the two built-ins. */
export function resolveTab(raw: ClientPricesTab | null, skuCount: number): ClientPricesTab {
  return raw ?? (skuCount > 0 ? 'products' : 'services');
}

/** Open the Client prices view, keeping whatever else the URL holds. */
export function withClientPricesView(prev: URLSearchParams): URLSearchParams {
  return updateSearchParams(prev, { [PRODUCTS_VIEW_PARAM]: CLIENT_PRICES_VIEW });
}

/** Back to Catalogue: drops the view and every Client prices param (D49). */
export function withoutClientPricesParams(prev: URLSearchParams): URLSearchParams {
  return updateSearchParams(prev, {
    [PRODUCTS_VIEW_PARAM]: null,
    ...Object.fromEntries(Object.values(PRICES_PARAM).map((key) => [key, null])),
  });
}

/**
 * Router state a bill's Set price link carries: the exact report URL it was
 * clicked on (tab, client, period, line, search), so Client prices can offer
 * "Back to bill". Router state, never a URL param: a pasted or bookmarked
 * Client prices URL, a reload of the report, or a new tab carries none, so the
 * back button shows only after a real trip from the bill (the PO list's
 * `ordersReturnTo` pattern).
 */
export interface BillReturnState {
  billReturnTo: string;
}

const REPORTING_PATH = '/reporting';
/** A longer value is not one this app wrote. */
const BILL_RETURN_MAX_LENGTH = 2048;

export function billReturnState(reportUrl: string): BillReturnState {
  return { billReturnTo: reportUrl };
}

/**
 * The report URL to go back to, or null. Accepts only the Reporting page
 * (`/reporting` or `/reporting?...`), so router state can never send the back
 * button anywhere else.
 */
export function readBillReturnTo(state: unknown): string | null {
  if (typeof state !== 'object' || state === null) return null;
  const value: unknown = (state as Record<string, unknown>).billReturnTo;
  if (typeof value !== 'string' || value.length > BILL_RETURN_MAX_LENGTH) return null;
  return value === REPORTING_PATH || value.startsWith(`${REPORTING_PATH}?`) ? value : null;
}

/**
 * The bill's Set price link (D25). One SKU: search to it and focus it. Several:
 * exactly those SKUs, focus the first. More than `PSKUS_MAX`: the link keeps
 * one more than the cap, so the page can say it shows only the first ones
 * while the URL stays bounded. `capped`: the bill listed only some of its
 * priceless SKUs (the server's cap), so the link always takes the `pskus` form
 * (even for one code) with `pmore=1`, and the page says it shows only the
 * first ones. A service: the Services tab, focused on that service's row.
 */
export function buildSetPriceHref({
  clientOrgId,
  tab,
  skus,
  capped = false,
  focus,
}: {
  clientOrgId: string | null;
  tab: ClientPricesTab;
  skus?: readonly string[];
  capped?: boolean;
  focus?: PriceFocus;
}): string {
  const params = new URLSearchParams();
  params.set(PRODUCTS_VIEW_PARAM, CLIENT_PRICES_VIEW);
  params.set(PRICES_PARAM.tab, tab);
  if (clientOrgId) params.set(PRICES_PARAM.pricesFor, clientOrgId);
  const codes = (skus ?? []).map((s) => s.trim()).filter((s) => s !== '');
  const [first] = codes;
  let target: PriceFocus | undefined = focus;
  if (first !== undefined) {
    if (codes.length === 1 && !capped) params.set(PRICES_PARAM.search, first);
    else params.set(PRICES_PARAM.skus, codes.slice(0, PSKUS_MAX + 1).join(','));
    if (capped) params.set(PRICES_PARAM.more, '1');
    target ??= { kind: 'sku', sku: first };
  }
  if (target) params.set(PRICES_PARAM.focus, serializePriceFocus(target));
  return `/products?${params.toString()}`;
}
